// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { userService, taskService } from '@/firebase/database';
import { useAuth } from '@/contexts/AuthContext';
import { db, isFirebaseConfigured } from '@/firebase/config';
import { collection, getDocs } from 'firebase/firestore';

const FirebaseTest: React.FC = () => {
  const { user } = useAuth();
  const [testResult, setTestResult] = useState<string>('');
  const [tasks, setTasks] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const testFirebaseConnection = async () => {
    if (!user) {
      setTestResult('Please login first to test Firebase');
      return;
    }

    if (!isFirebaseConfigured()) {
      setTestResult('❌ Firebase not configured. Please add Firebase environment variables to .env file');
      return;
    }

    setIsLoading(true);
    setTestResult('Testing Firebase connection...');

    try {
      // Test 1: Basic Firestore connection
      const testCollection = collection(db, 'test');
      await getDocs(testCollection);
      setTestResult('✅ Firebase connection successful!');

      // Test 2: User creation/update
      const userResult = await userService.createUser(user.id, {
        name: user.name,
        email: user.email,
        picture: user.picture,
        verified_email: user.verified_email
      });

      if (userResult.success) {
        setTestResult(prev => prev + ' ✅ User data saved successfully!');
        
        // Test 3: Task creation
        const taskResult = await taskService.createTask(user.id, {
          title: 'Test Task - ' + new Date().toLocaleTimeString(),
          description: 'This is a test task created from Firebase integration',
          completed: false,
          priority: 'medium',
          category: 'test'
        });

        if (taskResult.success) {
          setTestResult(prev => prev + ' ✅ Test task created successfully!');
          // Reload tasks to show the new one
          await loadTasks();
        } else {
          setTestResult(prev => prev + ' ❌ Task creation failed: ' + taskResult.error);
        }
      } else {
        setTestResult(prev => prev + ' ❌ User creation failed: ' + userResult.error);
      }
    } catch (error) {
      console.error('Firebase test error:', error);
      setTestResult('❌ Firebase connection failed: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTasks = async () => {
    if (!user) return;

    try {
      const result = await taskService.getTasks(user.id);
      if (result.success) {
        setTasks(result.data);
      } else {
        console.error('Failed to load tasks:', result.error);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      const result = await taskService.deleteTask(taskId);
      if (result.success) {
        await loadTasks(); // Reload tasks
        setTestResult('✅ Task deleted successfully!');
      } else {
        setTestResult('❌ Failed to delete task: ' + result.error);
      }
    } catch (error) {
      setTestResult('❌ Error deleting task: ' + error);
    }
  };

  useEffect(() => {
    if (user) {
      loadTasks();
    }
  }, [user]);

  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">🔥 Firebase Database Test</h2>
      
      <div className="space-y-6">
        {/* Test Button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={testFirebaseConnection}
            className="btn-primary"
            disabled={!user || isLoading}
          >
            {isLoading ? 'Testing...' : 'Test Firebase Connection'}
          </button>
          
          {user && (
            <div className="text-sm text-gray-600">
              Logged in as: <strong>{user.name}</strong>
            </div>
          )}
        </div>

        {/* Test Results */}
        {testResult && (
          <div className="p-4 bg-gray-100 rounded-md">
            <h3 className="font-semibold mb-2">Test Results:</h3>
            <p className="text-sm whitespace-pre-line">{testResult}</p>
          </div>
        )}

        {/* Tasks Display */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Your Tasks ({tasks.length})</h3>
            <button
              onClick={loadTasks}
              className="btn-secondary text-sm"
              disabled={!user}
            >
              Refresh Tasks
            </button>
          </div>
          
          {tasks.length > 0 ? (
            <div className="grid gap-3">
              {tasks.map((task) => (
                <div key={task.id} className="p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{task.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>Priority: {task.priority}</span>
                        <span>Status: {task.completed ? 'Completed' : 'Pending'}</span>
                        {task.category && <span>Category: {task.category}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="ml-4 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No tasks found.</p>
              <p className="text-sm mt-1">Test Firebase connection to create a test task.</p>
            </div>
          )}
        </div>

        {/* Firebase Status */}
        <div className="p-4 bg-blue-50 rounded-md">
          <h3 className="font-semibold text-blue-900 mb-2">Firebase Status</h3>
          <div className="text-sm text-blue-800 space-y-1">
            <p>✅ Firebase SDK: Installed</p>
            <p>✅ Firestore Database: Configured</p>
            <p>✅ Environment Variables: {user ? 'Loaded' : 'Not tested'}</p>
            <p>✅ Google OAuth: {user ? 'Connected' : 'Not connected'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirebaseTest;